import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Checkbox,
  CircularProgress,
  Collapse,
  Dialog,
  IconButton,
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import { BugReportDialog } from 'components/domain/feedback/BugReportDialog'
import { ContactOptions } from 'components/domain/feedback/ContactOptions'
import { Button } from 'components/facets'
import { useUser } from 'components/providers/UserProvider'
import DOMPurify from 'dompurify'
import { ChangelogEntry, getChangelog, markChangelogAsRead } from 'lib/api'
import { MouseEvent, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import './changelog-content.css'
import './news-presenter.css'

const getLocalizedTitle = (entry: ChangelogEntry, lang?: string): string => {
  switch (lang) {
    case 'es':
      return entry.titleEs || entry.title
    case 'fr':
      return entry.titleFr || entry.title
    case 'de':
      return entry.titleDe || entry.title
    case 'it':
      return entry.titleIt || entry.title
    default:
      return entry.title
  }
}

const getLocalizedBody = (entry: ChangelogEntry, lang?: string): string => {
  switch (lang) {
    case 'es':
      return entry.bodyEs || entry.body
    case 'fr':
      return entry.bodyFr || entry.body
    case 'de':
      return entry.bodyDe || entry.body
    case 'it':
      return entry.bodyIt || entry.body
    default:
      return entry.body
  }
}

const stripHtml = (html: string): string => {
  const doc = new DOMParser().parseFromString(html, 'text/html')
  return doc.body.textContent || ''
}

const isPlainText = (text: string): boolean => !/<[a-z][\s\S]*>/i.test(text)

// A link written in an announcement points inside the application, and going
// through the browser would reload it from scratch — splash screen included —
// instead of moving to the page.
const internalPath = (link: HTMLAnchorElement): string | null => {
  // The editor puts target="_blank" on every link it creates, and a link into
  // the application has no business opening a tab of its own.
  const href = link.getAttribute('href')
  if (!href) return null
  if (href.startsWith('/')) return href

  try {
    const url = new URL(href, window.location.origin)

    return url.origin === window.location.origin ? `${url.pathname}${url.search}${url.hash}` : null
  } catch {
    return null
  }
}

const SANITIZE_OPTIONS = {
  ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 'h1', 'h2', 'h3', 'ul', 'ol', 'li', 'a', 'img'],
  ALLOWED_ATTR: ['href', 'target', 'src', 'alt', 'width', 'height', 'class'],
}

export const NewsPresenter = (): JSX.Element => {
  const { t } = useTranslation()
  const { user } = useUser()
  const [entries, setEntries] = useState<ChangelogEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [openEntry, setOpenEntry] = useState<ChangelogEntry | null>(null)
  const history = useHistory()
  const [zoomedImage, setZoomedImage] = useState<string | null>(null)
  const [showContactMenu, setShowContactMenu] = useState(false)
  const [openBugReportDialog, setOpenBugReportDialog] = useState(false)
  const [expanded, setExpanded] = useState(() => {
    try {
      return localStorage.getItem('news-presenter-collapsed') !== 'true'
    } catch {
      return true
    }
  })

  const handleAccordionChange = (_: unknown, isExpanded: boolean) => {
    setExpanded(isExpanded)
    try {
      localStorage.setItem('news-presenter-collapsed', String(!isExpanded))
    } catch {
      // ignore
    }
  }

  useEffect(() => {
    getChangelog()
      .then(setEntries)
      .catch(() => setEntries([]))
      .finally(() => setLoading(false))
  }, [])

  const unreadCount = entries.filter((e) => !e.isRead).length

  const handleMarkAsRead = async (id: number) => {
    try {
      await markChangelogAsRead(id)
      setEntries((prev) =>
        prev.map((entry) => (entry.id === id ? { ...entry, isRead: true } : entry))
      )
    } catch {
      // ignore
    }
  }

  return (
    <>
      <Accordion
        data-testid="news-presenter"
        className="news-presenter"
        expanded={expanded}
        onChange={handleAccordionChange}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} className="news-presenter__header">
          <div className="d-flex align-items-center">
            <h5 className="m-0 fw-semibold">{t('dashboard.news')}</h5>
            {unreadCount > 0 && (
              <span className="news-presenter__unread-count" style={{ marginLeft: 16 }}>
                {unreadCount}
              </span>
            )}
          </div>
        </AccordionSummary>
        <AccordionDetails className="news-presenter__details">
          {loading ? (
            <div className="d-flex justify-content-center py-2 w-100">
              <CircularProgress size={24} />
            </div>
          ) : entries.length === 0 ? (
            <span className="small text-muted d-block">{t('dashboard.noNews')}</span>
          ) : (
            <div className="w-100">
              {[...entries]
                .sort((a, b) => Number(a.isRead) - Number(b.isRead))
                .map((entry) => (
                  <div
                    key={entry.id}
                    className={`news-presenter__item mb-2 ${
                      entry.isRead ? 'news-presenter__item--read' : ''
                    }`}
                    onClick={() => {
                      setOpenEntry(entry)
                      if (!entry.isRead) handleMarkAsRead(entry.id)
                    }}
                  >
                    <span className="news-presenter__title d-block fw-semibold">
                      {getLocalizedTitle(entry, user?.language)}
                    </span>
                    <span className="news-presenter__body small mt-1">
                      {stripHtml(getLocalizedBody(entry, user?.language))}
                    </span>
                    <div className="d-flex justify-content-between align-items-center mt-3">
                      <span className="news-presenter__date small">{entry.date}</span>
                      {!entry.isRead && (
                        <span
                          className="news-presenter__checkbox-label"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Checkbox
                            size="small"
                            checked={false}
                            onChange={() => handleMarkAsRead(entry.id)}
                          />
                          {t('dashboard.viewed')}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
            </div>
          )}
        </AccordionDetails>
      </Accordion>

      <Dialog
        open={!!openEntry}
        onClose={() => {
          setOpenEntry(null)
          setShowContactMenu(false)
        }}
        maxWidth="md"
        fullWidth
        disableEnforceFocus
      >
        <IconButton
          aria-label="close"
          style={{ position: 'absolute', right: 12, top: 12, color: '#9e9e9e' }}
          onClick={() => {
            setOpenEntry(null)
            setShowContactMenu(false)
          }}
        >
          <CloseIcon />
        </IconButton>
        <Box padding={4} minWidth="400px">
          {openEntry && (
            <>
              <span className="small text-muted">{openEntry.date}</span>
              <h3 className="mt-1 mb-2 fw-semibold">
                {getLocalizedTitle(openEntry, user?.language)}
              </h3>
              {(() => {
                const body = getLocalizedBody(openEntry, user?.language)
                if (isPlainText(body)) {
                  return <p style={{ whiteSpace: 'pre-wrap' }}>{body}</p>
                }
                const sanitized = DOMPurify.sanitize(body, SANITIZE_OPTIONS)
                return (
                  <div
                    className="changelog-content"
                    onClick={(e: MouseEvent<HTMLDivElement>) => {
                      const target = e.target as HTMLElement
                      if (target.tagName === 'IMG') {
                        setZoomedImage((target as HTMLImageElement).src)

                        return
                      }

                      // The click lands on the text inside the link, not on the link.
                      const link = target.closest('a')
                      const path = link && internalPath(link)
                      if (!path) return

                      e.preventDefault()
                      setOpenEntry(null)
                      history.push(path)
                    }}
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    dangerouslySetInnerHTML={{ __html: sanitized }}
                  />
                )
              })()}

              <div className="d-flex justify-content-end align-items-center mt-3 pt-3 border-top">
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setShowContactMenu((prev) => !prev)}
                >
                  <i className="fa-light fa-message me-2" />
                  {t('navigation.contactUs')}
                </Button>
              </div>

              <Collapse in={showContactMenu}>
                <div className="mt-3">
                  <ContactOptions
                    onSupportRequest={() => {
                      setOpenEntry(null)
                      setShowContactMenu(false)
                      setOpenBugReportDialog(true)
                    }}
                  />
                </div>
              </Collapse>
            </>
          )}
        </Box>
      </Dialog>

      <BugReportDialog open={openBugReportDialog} onClose={() => setOpenBugReportDialog(false)} />

      {zoomedImage && (
        <div className="changelog-image-zoom-backdrop" onClick={() => setZoomedImage(null)}>
          <img src={zoomedImage} alt="" />
        </div>
      )}
    </>
  )
}
